"use client"

import { useEffect, useState } from "react"
import { Heart, Facebook, Twitter, Mail } from "lucide-react"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { useWishlist } from "@lib/context/wishlist-context"
import Breadcrumbs from "@modules/common/components/breadcrumbs"
import AccountNav from "@modules/account/components/account-nav"
import { HttpTypes } from "@medusajs/types"
import { getBaseURL } from "@lib/util/env"
import WishlistCard, {
  WishlistProduct,
} from "@modules/wishlist/components/wishlist-card"

type Props = {
  customer: HttpTypes.StoreCustomer | null
}

export default function WishlistTemplate({ customer }: Props) {
  const { items, remove } = useWishlist()
  const [products, setProducts] = useState<WishlistProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [newWishlistName, setNewWishlistName] = useState("")

  useEffect(() => {
    if (items.length === 0) {
      setProducts([])
      setLoading(false)
      return
    }

    const fetchProducts = async () => {
      setLoading(true)
      try {
        const params = new URLSearchParams()
        params.set("limit", String(items.length))
        // No region_id: /api/search resolves the customer's current region
        // server-side and ignores anything sent from here (#166532).
        params.set("fields", "*variants.calculated_price")
        items.forEach((id) => params.append("id[]", id))
        const res = await fetch(`/api/search?${params.toString()}`)
        if (res.ok) {
          const data = await res.json()
          setProducts(data.products || [])
        }
      } catch {
        setProducts([])
      } finally {
        setLoading(false)
      }
    }

    fetchProducts()
  }, [items])

  const shareUrl = `${getBaseURL()}/wishlist`

  return (
    <div className="bg-white min-h-[60vh]">
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-6 flex flex-col lg:flex-row gap-8">
        {/* Sidebar — hidden below `small`; also enforced inside AccountNav itself */}
        <div className="hidden small:block small:w-[280px] flex-shrink-0">
          <AccountNav customer={customer} />
        </div>

        <div className="flex-1 min-w-0">
          <Breadcrumbs
            items={[{ label: "Home", href: "/" }, { label: "My Wishlist" }]}
          />

          <div className="flex items-center gap-3 mt-4 mb-2">
            <Heart className="w-7 h-7 text-brand-accent fill-brand-accent" />
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
              My Wishlist
            </h1>
          </div>

          {items.length > 0 && (
            <div className="mb-8">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-gray-500">
                  Create your wishlist and share it on social media
                </p>
                <div className="flex items-center gap-2">
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                      shareUrl
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:opacity-90 transition-opacity"
                    aria-label="Share wishlist on Facebook"
                  >
                    <Facebook className="w-4 h-4" />
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(
                      shareUrl
                    )}&text=${encodeURIComponent("Check out my wishlist")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-[#1DA1F2] text-white flex items-center justify-center hover:opacity-90 transition-opacity"
                    aria-label="Share wishlist on Twitter"
                  >
                    <Twitter className="w-4 h-4" />
                  </a>
                  <a
                    href={`mailto:?subject=${encodeURIComponent(
                      "Check out my wishlist"
                    )}&body=${encodeURIComponent(shareUrl)}`}
                    className="w-9 h-9 rounded-full bg-gray-700 text-white flex items-center justify-center hover:opacity-90 transition-opacity"
                    aria-label="Share wishlist by email"
                  >
                    <Mail className="w-4 h-4" />
                  </a>
                </div>
              </div>

              {customer && (
                <div className="flex flex-wrap items-center gap-3 mt-4">
                  <select
                    className="h-10 rounded-md border border-gray-300 px-3 text-sm text-gray-700"
                    defaultValue="default"
                    aria-label="Select wishlist"
                  >
                    <option value="default">
                      {customer.first_name} Default Wishlist
                    </option>
                  </select>

                  <input
                    type="text"
                    value={newWishlistName}
                    onChange={(e) => setNewWishlistName(e.target.value)}
                    placeholder="Wishlist Name"
                    className="h-10 rounded-md border border-gray-300 px-3 text-sm w-48"
                  />
                  <button
                    type="button"
                    className="h-10 px-5 rounded-md bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors"
                  >
                    Create
                  </button>

                  <button
                    type="button"
                    className="h-10 px-5 rounded-md bg-gray-900 text-white text-sm font-semibold hover:bg-black transition-colors"
                  >
                    Add Products
                  </button>
                  <button
                    type="button"
                    className="h-10 px-5 rounded-md border border-red-500 text-red-600 text-sm font-semibold hover:bg-red-50 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/5] bg-gray-100 rounded-lg mb-3" />
                  <div className="h-4 bg-gray-100 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-gray-100 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-20">
              <Heart className="w-16 h-16 text-gray-200 mx-auto mb-6" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Your wishlist is empty
              </h2>
              <p className="text-gray-500 mb-8 max-w-md mx-auto">
                Browse our collection and tap the heart icon on any product to
                save it here for later.
              </p>
              <LocalizedClientLink
                href="/store"
                className="inline-flex items-center justify-center px-8 py-3 bg-brand-hero-navy text-white font-semibold rounded-full hover:bg-brand-primary-blue transition-colors"
              >
                Browse Products
              </LocalizedClientLink>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {products.map((product) => (
                <WishlistCard
                  key={product.id}
                  product={product}
                  onRemove={remove}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
